// Keeping the signed device list in step with Devices > Delete (2026-09-19).
//
// There is no separate "a monitored device was lost" action any more: a lost
// or retired device is simply DELETED on the Devices page. That is what locks
// it out (the service refuses its uploads and settings reads, and the app
// wipes itself if it ever checks in) - a monitored device never held a
// decryption key, so nothing has to be rotated or purged.
//
// What a delete cannot do by itself is touch the owner-signed device list:
// only the owner's unlocked browser can re-sign it. So this module plans the
// follow-up the browser makes:
//
//   1. TOMBSTONE (`revokedAt`) the entry of every deleted device. Not removed
//      yet on purpose: the viewer needs the entry's public key to open the
//      screenshots that device uploaded BEFORE it was deleted, and those stay
//      on the account for 15 days. An entry that is gone makes them fail as
//      "unknown device".
//   2. DROP a tombstoned entry once DEVICE_TOMBSTONE_RETENTION_MS has passed
//      (twice the 15-day record lifetime), when nothing it authored exists
//      any more. From then on the list holds only the account's real devices.
//
// Two ways an entry is chosen for a tombstone:
//   - explicitly, by device id, right after a delete on the Devices page;
//   - by the catch-up sweep, for deletes this browser could not sign at the
//     time (locked browser, an admin's browser, an uninstall): a live entry
//     whose device is no longer registered on the account.
// The sweep is an INFERENCE and a wrong tombstone is permanent for that
// install (an uploader latches "revoked" until it is registered again), so
// it is deliberately conservative: it never touches an entry younger than
// DEVICE_SWEEP_GRACE_MS (an approval and the registration it belongs to do
// not land in the same instant), and the caller must pass a COMPLETE,
// freshly fetched set of registered device ids or none at all.
//
// Pure module (no Vue / API imports) so Node can exercise it directly.

export const DEVICE_TOMBSTONE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000
export const DEVICE_SWEEP_GRACE_MS = 24 * 60 * 60 * 1000

const lower = (s) => String(s || '').toLowerCase()
const isMs = (n) => Number.isSafeInteger(n) && n > 0

/**
 * The device UUID inside a legacy device id. The account row's
 * `identities[].id` and a device's `deviceId` are the same string: reversed
 * `cognitoId|deviceUUID`. A signed device-list entry's `deviceId` is that
 * deviceUUID. Returns null when the id is not in that form.
 */
export function deviceUuidFromLegacyId (legacyId) {
  const raw = [...String(legacyId || '')].reverse().join('')
  const uuid = raw.split('|')[1]
  return uuid || null
}

/**
 * Plan one re-signing of `deviceList`. Returns `{ tombstone, prune }` - the
 * `deviceId`s to mark revoked and the ones to drop. Both empty = no write.
 *
 *   deletedDeviceIds     device UUIDs the caller just deleted (explicit).
 *   registeredDeviceIds  the COMPLETE set of device UUIDs registered on the
 *                        account right now, or null to skip the sweep.
 */
export function planDeviceRetirement (deviceList, { deletedDeviceIds = [], registeredDeviceIds = null, nowMs = Date.now() } = {}) {
  const devices = (deviceList && deviceList.devices) || []
  const deleted = new Set([...deletedDeviceIds].map(lower).filter(Boolean))
  const registered = registeredDeviceIds ? new Set([...registeredDeviceIds].map(lower).filter(Boolean)) : null
  const tombstone = []
  const prune = []
  for (const d of devices) {
    const id = lower(d.deviceId)
    if (d.revokedAt != null) {
      if (isMs(d.revokedAt) && d.revokedAt <= nowMs - DEVICE_TOMBSTONE_RETENTION_MS) prune.push(d.deviceId)
      continue
    }
    if (deleted.has(id)) { tombstone.push(d.deviceId); continue }
    // An entry with no usable `addedAt` cannot prove it is past the grace, so the sweep leaves it.
    const settled = isMs(d.addedAt) && d.addedAt <= nowMs - DEVICE_SWEEP_GRACE_MS
    if (registered && settled && !registered.has(id)) tombstone.push(d.deviceId)
  }
  return { tombstone, prune }
}

/** Apply a plan in place (the caller bumps the version and re-signs). */
export function applyDeviceRetirement (deviceList, plan, nowMs = Date.now()) {
  const drop = new Set(plan.prune)
  const mark = new Set(plan.tombstone)
  deviceList.devices = deviceList.devices.filter((d) => !drop.has(d.deviceId))
  for (const d of deviceList.devices) if (mark.has(d.deviceId) && d.revokedAt == null) d.revokedAt = nowMs
  return deviceList
}
